import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Download, Copy, Share2, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { certError, downloadCert, waUrl, canShareFiles, nativeSharePdf } from "@/lib/certs";
import { WhatsAppDirectSend } from "@/components/certs/WhatsAppDirectSend";

export const CertShareDialog = ({ cert, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (!cert) return;
    api.get(`/certificates/${cert.id}/share`).then(({ data: d }) => setData(d))
      .catch(async (e) => { toast.error(await certError(e)); onClose(); });
  }, [cert, onClose]);

  const copy = async () => { await navigator.clipboard.writeText(data.message); toast.success("Message copied"); };
  const dl = async () => { try { await downloadCert(cert); } catch (e) { toast.error(await certError(e)); } };
  const native = async () => { try { await nativeSharePdf(cert, data.message); } catch (e) { if (e?.name !== "AbortError") toast.error("Sharing was cancelled or isn't supported here."); } };

  return (
    <Dialog open={!!cert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-card" data-testid="cert-share-dialog">
        <DialogHeader><DialogTitle className="font-head">Share Certificate on WhatsApp</DialogTitle></DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground">Preparing message…</p> : (
          <div className="space-y-3">
            <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg border border-border bg-background p-3 font-body text-xs leading-relaxed" data-testid="cert-share-message">{data.message}</pre>
            <WhatsAppDirectSend cert={cert} />
            <p className="flex items-start gap-2 rounded-lg bg-amber-500/10 p-2.5 text-xs text-amber-500" data-testid="cert-share-note">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />WhatsApp opens with this message and the verification link. The PDF is not attached automatically: download it and attach it in the chat{canShareFiles() ? ", or use Share PDF on this device" : ""}.
            </p>
            {!data.phone && <p className="text-xs text-muted-foreground">No mobile number on file, so WhatsApp will ask you to pick a contact.</p>}
            <div className="flex flex-wrap gap-2">
              <Button asChild className="bg-emerald-600 hover:bg-emerald-700" data-testid="cert-share-open-whatsapp">
                <a href={waUrl(data.phone, data.message)} target="_blank" rel="noreferrer"><MessageCircle className="mr-1.5 h-4 w-4" /> Open WhatsApp</a>
              </Button>
              <Button variant="outline" onClick={dl} data-testid="cert-share-download"><Download className="mr-1.5 h-4 w-4" /> Download PDF</Button>
              {canShareFiles() && <Button variant="outline" onClick={native} data-testid="cert-share-native"><Share2 className="mr-1.5 h-4 w-4" /> Share PDF</Button>}
              <Button variant="ghost" onClick={copy} data-testid="cert-share-copy"><Copy className="mr-1.5 h-4 w-4" /> Copy</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
